import { useState, KeyboardEvent } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

interface InputBarProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
}

export default function InputBar({ onSendMessage, disabled = false }: InputBarProps) {
  const { theme } = useTheme();
  const [message, setMessage] = useState('');

  const handleSend = () => {
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className={`fixed bottom-0 left-0 right-0 border-t backdrop-blur-xl ${
      theme === 'dark'
        ? 'bg-black/40 border-white/10'
        : 'bg-white/80 border-gray-200'
    }`}>
      <div className="max-w-md mx-auto px-4 py-3">
        <div className={`flex items-end gap-2 rounded-2xl px-4 py-2 ${
          theme === 'dark'
            ? 'bg-white/5 border border-white/10'
            : 'bg-gray-50 border border-gray-200'
        }`}>
          {/* Textarea */}
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            placeholder="Answer here..."
            disabled={disabled}
            rows={1}
            className={`flex-1 bg-transparent resize-none outline-none text-sm py-2 max-h-24 ${
              theme === 'dark'
                ? 'text-white placeholder-white/40'
                : 'text-gray-900 placeholder-gray-400'
            }`}
            style={{
              minHeight: '24px',
              maxHeight: '96px'
            }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = `${Math.min(target.scrollHeight, 96)}px`;
            }}
          />

          {/* Botón enviar minimal */}
          <button
            onClick={handleSend}
            disabled={!message.trim() || disabled}
            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              message.trim() && !disabled
                ? theme === 'dark'
                  ? 'bg-gradient-to-br from-purple-500 to-pink-500 hover:shadow-lg hover:shadow-purple-500/50'
                  : 'bg-gradient-to-br from-purple-400 to-pink-400 hover:shadow-lg hover:shadow-purple-400/50'
                : theme === 'dark'
                  ? 'bg-white/5'
                  : 'bg-gray-200'
            }`}
          >
            <i className={`ri-send-plane-fill text-sm ${
              message.trim() && !disabled
                ? 'text-white'
                : theme === 'dark'
                  ? 'text-white/30'
                  : 'text-gray-400'
            }`}></i>
          </button>
        </div>

        {/* Hint de Enter */}
        <p className={`text-xs text-center mt-2 ${
          theme === 'dark' ? 'text-white/30' : 'text-gray-400'
        }`}>
          Press Enter to send
        </p>
      </div>
    </div>
  );
}
